import { useAnalytics } from '@ecosystem/newron-client-lib';
import { cloneDeep, isEqual } from 'lodash';
import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import BrandDependencyCard from '@pages/trading-assistant/brand-dependency-card/BrandDependencyCard';
import BrandSelector from '@components/brand-selector/BrandSelector';
import ComparisonGrowthSelector from '@components/comparison-growth-selector/ComparisonGrowthSelector';
import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import { RelativePeriod, RelativePeriodLabel2 } from '@components/comparison-period-selector/Types';
import FilterChips from '@components/filter-chips/FilterChips';
import FilterSelectionNote from '@components/filter-selection-note/FilterSelectionNote';
import TimeFramePickerWrapper from '@components/time-frame-picker/components/wrapper/TimeFramePickerWrapper';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { createDataLookup } from '@data-visualization/brand-dependency-chart/BrandDependencyChartUtils';
import {
  BrandDeepDiveChartResp,
  BrandDeepDiveChartRespDataLookup,
  BrandDependencyChartResp,
  BrandDependencyChartSubResp,
  BrandDependencyKpi,
  BrandDependencyReqParam,
  BrandDependencySort,
  INITIAL_BY_SEGMENT_OPTION,
} from '@models/BrandDependency';
import { SalesPerformance } from '@models/SalesPerformance';
import { getEffectiveProductType } from '@models/SubscribedProductType';
import { getBrandDeepDive, getBrandDependency } from '@services/BrandDependency.service';
import { BaseTimePeriod, MainFilters } from '@services/FiltersTypes';
import { actionCreatorsMarketPremium, actionCreatorsSideFilters } from '@state/Index';
import { IRootState } from '@state/reducers/Reducers';
import { appliedCountry, getCountryFilterPairsByTimeFrame } from '@utils/CountryUtils';
import { emptyBrandFilter, getAppliedFiltersFromPagesConfig } from '@utils/FilterUtils';
import saveErrorLog, { ErrorTagEnum } from '@utils/LogUtil';
import { getVolumeBasedOnPeriodType } from '@utils/factVolumeUtils/FactVolumeUtils';
import {
  getCarryOverAllFiltersForApply,
  getExcludedPagesToCarryOver,
  updateAppliedFilters,
  updateSelectedBrandFilter,
} from '@utils/filterTrayUtils/FilterTrayUtils';
import { usePrevious } from '@utils/hook/common/CommonHooks';
import { useFetchSalesPerformance } from '@utils/hook/marketPerformance/UseFetchSalesPerformance';
import { FilterName, FilterType } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import {
  GLOBAL_FILTER_APPLIED_TRACKING_TITLE,
  globalFiltersTrackingFunction,
  GlobalFiltersTrackingFunctionParam,
} from '@utils/page-analytics/model/GlobalFiltersTracking';
import {
  topPageFiltersTrackingFunction,
  TopPageFiltersTrackingFunctionParam,
  useGenerateTopPageFiltersTrackingParam,
} from '@utils/page-analytics/model/TopPageFiltersTracking';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { byFilterKey, excludeByFilterKey } from '@utils/pageConfig/Predicates/SelectedFiltersPredicates';
import {
  CountryFilterPair,
  FeatureOption,
  FilterKeys,
  Filters,
  getPageTypeWithPageName,
  Option,
  PageName,
  PageType,
  SelectedFilters,
  SubSection,
} from '@utils/pageConfig/Types';
import { ProductDataType } from '@pages/product-analyzer/Product';
import WarningBrandsContainer from '../warning-brands-container/WarningBrandsContainer';
import { NO_OPTIONS } from '@utils/StringConstants';
import PgAvailabilityBanner from '@components/pg-availability-banner/PgAvailabilityBanner';
import {
  PgAvailabilityProps,
  useDispatchPgsAvailability,
} from '@utils/hook/incrementalLoading/UseDispatchPgsAvailability';
import MarketPerformancePosFactsTiles from '@components/market-performance-pos-facts-tiles/MarketPerformancePosFactsTiles';
import { CreateInsightDataFn } from '@ecosystem/newron-client-collections';
import { TradingAssistantButterflyChartInsight } from '@components/collections/trading-assistant-butterfly-chart/Model';
import { TRADING_ASSISTANT_CARD_TITLE } from '@pages/Utilities';
import {
  getSortedDeepDiveBrands,
  useBrandsInFilter,
  useFilteredBrands,
} from '@pages/trading-assistant/supplier-review/SupplierReviewUtils';
import { CustomMonthsPeriodState } from '@state/reducers/main-filter/MainFiltersReducer';
import { applyTimeFramePicker } from '@components/time-frame-picker/utils/TimeFramePickerUtils';
import { useUpdatePerfPeriodSet } from '@utils/period-set/UseUpdatePerfPeriodSet';
import { useFactRevenueBySelectedCurrency } from '@hooks/UseFactRevenueBySelectedCurrency';

interface Props {
  filters: MainFilters;
  timeFrame: Periodicity;
  setTimeFrame: Dispatch<SetStateAction<Periodicity>>;
  comparisonPeriod: RelativePeriod | BaseTimePeriod;
  setComparisonPeriod: Dispatch<SetStateAction<RelativePeriod | BaseTimePeriod>>;
  period: BaseTimePeriod;
  setPeriod: Dispatch<SetStateAction<BaseTimePeriod>>;
  pgListByCountry: CountryFilterPair[];
  appliedCountries: Option[];
  appliedProductGroups: Option[] | undefined;
  timeFrameOnChangeHandler: any;
  isLoadingBrands: boolean;
  brandFilterOptions: Filters;
  features: Filters[];
  featureOption: FeatureOption;
  setFeatureOptions: React.Dispatch<React.SetStateAction<FeatureOption>>;
  isVirtualBrandList?: boolean;
  brandDependencyState: any;
  viewingBrand: string;
  setViewingBrand: (brand: string, source?: 'restore' | 'kpi_selector' | 'brand_selector') => void;
  selectedGrowthType: GrowthTypes;
  setSelectedGrowthType: (growthType: GrowthTypes) => void;
  kpi: BrandDependencyKpi;
  setKpi: React.Dispatch<SetStateAction<BrandDependencyKpi>>;
  sortBy: BrandDependencySort;
  setSortBy: React.Dispatch<SetStateAction<BrandDependencySort>>;
  customMonthsPeriodState?: CustomMonthsPeriodState | null;
  customWeeksPeriodState?: CustomMonthsPeriodState | null;
  sideFiltersState?: any;
  isDeepDiveView: boolean;
  setIsDeepDiveView: React.Dispatch<SetStateAction<boolean>>;
}

const SupplierReview: React.FC<Props> = ({
  filters,
  timeFrame,
  setTimeFrame,
  comparisonPeriod,
  setComparisonPeriod,
  period,
  setPeriod,
  pgListByCountry,
  appliedCountries,
  appliedProductGroups,
  timeFrameOnChangeHandler,
  isLoadingBrands,
  brandFilterOptions,
  features,
  featureOption,
  setFeatureOptions,
  isVirtualBrandList = true,
  viewingBrand,
  setViewingBrand,
  brandDependencyState,
  selectedGrowthType,
  setSelectedGrowthType,
  kpi,
  setKpi,
  sortBy,
  setSortBy,
  customMonthsPeriodState,
  customWeeksPeriodState,
  sideFiltersState,
  isDeepDiveView,
  setIsDeepDiveView,
}) => {
  const pagesConfig = usePageConfig();
  const { track } = useAnalytics();
  const topPageFiltersTrackingParam = useGenerateTopPageFiltersTrackingParam(
    PageName.TRADING_ASSISTANT,
    PageType.TRADING_ASSISTANT,
  );

  const subscribedProductTypes = useSelector(
    (state: IRootState) => state.subscribedProductTypes.subscribedProductTypes,
  );
  const effectiveProductType = getEffectiveProductType(subscribedProductTypes, PageName.TRADING_ASSISTANT);
  const mainFilterMarketPremiumState = useSelector((state: IRootState) => state.mainFiltersMarketPremium);
  const retailerClientState = useSelector((state: IRootState) => state.marketPremiumRetailerClient);

  const [showBrandsWarning, setShowBrandsWarning] = useState<boolean>(false);
  const [isResettingBrands, setIsResettingBrands] = useState<boolean>(false);
  const MAX_BRANDS_FILTER = 10;

  const factRevenueBySelectedCurrency = useFactRevenueBySelectedCurrency(
    pagesConfig,
    appliedCountries,
    effectiveProductType,
  );

  // Market Performance Cards states
  const [revenuePerformance, setRevenuePerformance] = useState(new SalesPerformance());
  const [volumePerformance, setVolumePerformance] = useState(new SalesPerformance());
  const [isApiError, setIsApiError] = useState<boolean>(false);

  const [loadingPosFacts, setLoadingPosFacts] = useState<boolean>(true);
  const [defaultFilters] = useState<MainFilters[]>(
    useSelector((state: IRootState) => state.initialFiltersMarketPremium).initialFilters,
  );

  const [loadingChart, setLoadingChart] = useState<boolean>(true);
  const [chartData, setChartData] = useState<BrandDependencyChartResp>();
  const [deepDiveChartData, setDeepDiveChartData] = useState<BrandDeepDiveChartResp>();
  const [deepDiveDataLookup, setDeepDiveDataLookup] = useState<Map<string, BrandDeepDiveChartRespDataLookup>>(
    new Map(),
  );
  const prevCountriesAndPGs = usePrevious({ appliedCountries, appliedProductGroups });
  const createViewState = useCallback(() => brandDependencyState, [brandDependencyState]);
  // Redux
  const dispatch = useDispatch();
  const { updateMainFiltersMarketPremium } = bindActionCreators(actionCreatorsMarketPremium, dispatch);
  const { updateSideFilters } = bindActionCreators(actionCreatorsSideFilters, dispatch);
  const periodOptions = filters?.timePeriod;
  const {
    updateComparisonPeriod,
    updatePeriodicity,
    updatePeriod,
    updateCustomMonthsPeriodState,
    updateCustomWeeksPeriodState,
  } = useUpdatePerfPeriodSet();
  const onChangeSelectedGrowthType = (growthType: GrowthTypes) => {
    setSelectedGrowthType(growthType);
    updateMainFiltersMarketPremium({
      timeFrame,
      period: mainFilterMarketPremiumState.period,
      selectedGrowthType: growthType,
    });
  };

  const applyTimeFrameCallback = (
    periodicity: Periodicity,
    baseTimePeriod: BaseTimePeriod,
    relativePeriod: RelativePeriod | BaseTimePeriod,
  ) => {
    setTimeFrame(periodicity);
    setPeriod(baseTimePeriod);
    setComparisonPeriod(relativePeriod);

    const isNotMATAndYTD: boolean = periodicity !== Periodicity.MAT && periodicity !== Periodicity.YTD;

    const tracker: TopPageFiltersTrackingFunctionParam = {
      ...topPageFiltersTrackingParam,
      periodicity,
      focusPeriod: baseTimePeriod.label,
      ...(isNotMATAndYTD && {
        comparisonPeriod:
          RelativePeriodLabel2[relativePeriod as RelativePeriod] ?? (relativePeriod as BaseTimePeriod).label,
      }),
      access: pagesConfig.access,
    };
    topPageFiltersTrackingFunction(tracker);
    applyTimeFramePicker({
      givenPeriodicity: periodicity,
      givenPeriod: baseTimePeriod,
      givenComparison: relativePeriod,
      pageType: getPageTypeWithPageName(PageName.TRADING_ASSISTANT),
      pageName: PageName.TRADING_ASSISTANT,
      pagesConfig: pagesConfig,
      countryChanged: false,
      sideFiltersState: sideFiltersState,
      retailerClientState: retailerClientState,
      track: track,
      setTimeFrame: updatePeriodicity,
      setPeriod: updatePeriod,
      setRelativePeriod: updateComparisonPeriod,
      setCountryChanged: () => {},
      setCustomMonthPeriodState: updateCustomMonthsPeriodState,
      setCustomWeekPeriodState: updateCustomWeeksPeriodState,
    });
  };

  const brandFilterTracking = (appliedFilters: SelectedFilters[]) => {
    const excludedPagesForCarryOverFilters = getExcludedPagesToCarryOver(pagesConfig);
    const params: GlobalFiltersTrackingFunctionParam = {
      eventTitle: GLOBAL_FILTER_APPLIED_TRACKING_TITLE,
      sideFilters: getCarryOverAllFiltersForApply(
        appliedFilters,
        PageType.TRADING_ASSISTANT,
        undefined,
        excludedPagesForCarryOverFilters,
      ),
      retailerClientState,
      track,
      pageType: PageType.TRADING_ASSISTANT,
      pageName: PageName.TRADING_ASSISTANT,
      filterName: FilterName.GLOBAL_FILTER,
      filterType: FilterType.PLATFORM,
      access: pagesConfig.access,
    };

    globalFiltersTrackingFunction(params);
  };

  const brandFiltersWithDefaultValue: Filters = brandFilterOptions ?? emptyBrandFilter;

  const getBrandsInFilter = useBrandsInFilter(brandFiltersWithDefaultValue);

  const getFilteredBrands = useFilteredBrands(brandFiltersWithDefaultValue);

  const updateBrandsInFilters = (toUpdate: Filters) => {
    setShowBrandsWarning(false);
    const selectedFilters = updateSelectedBrandFilter(pagesConfig, pagesConfig.pageType, toUpdate);
    const appliedFilters = updateAppliedFilters(pagesConfig, toUpdate, selectedFilters);
    updateSideFilters({ appliedFilters });
    return appliedFilters;
  };

  const clearBrandsInFilters = () => {
    const { appliedFilters, selectedFilters, setAppliedFilters, setSelectedFilters } = pagesConfig;

    if (!appliedFilters[PageType.TRADING_ASSISTANT].selectedFilters.find(byFilterKey(FilterKeys.BRAND))) {
      return;
    }

    const cloneAppliedFilters = [...appliedFilters];
    const appliedSlice = structuredClone(cloneAppliedFilters[PageType.TRADING_ASSISTANT]);
    cloneAppliedFilters[PageType.TRADING_ASSISTANT].selectedFilters = appliedSlice.selectedFilters.filter(
      excludeByFilterKey(FilterKeys.BRAND),
    );
    setAppliedFilters(cloneAppliedFilters);

    const cloneSelectedFilters = structuredClone(selectedFilters);
    const selectedSlice = cloneSelectedFilters[PageType.TRADING_ASSISTANT];
    cloneSelectedFilters[PageType.TRADING_ASSISTANT].selectedFilters = selectedSlice.selectedFilters.filter(
      excludeByFilterKey(FilterKeys.BRAND),
    );
    setSelectedFilters(cloneSelectedFilters);
  };

  const onBrandsChangeFunc = (toUpdate: Filters) => {
    const appliedFilters = updateBrandsInFilters(toUpdate);
    brandFilterTracking(appliedFilters);
  };

  const getBrandDependencyChartData = (
    selectedBrands: Option[],
    chartKpi: BrandDependencyKpi,
    chartResp?: BrandDependencyChartResp,
  ) => {
    if (selectedBrands && selectedBrands.length > 0) {
      return chartResp?.selectedBrandsData;
    }
    return chartResp?.[chartKpi];
  };

  //Props for custom hooks to dispatch pgs availability details
  const pgsAvailabilityProps: PgAvailabilityProps = useMemo(
    () => ({
      periodValue: period.value,
      periodicity: timeFrame,
      appliedCountries,
      pgsByAppliedCountry: pagesConfig.pgsByAppliedCountry,
      countriesPgsAvailabilities: pagesConfig.masterPgsByCountry,
      appliedProductGroups: appliedProductGroups ?? [],
    }),
    [
      period.value,
      timeFrame,
      appliedCountries,
      pagesConfig.pgsByAppliedCountry,
      pagesConfig.masterPgsByCountry,
      appliedProductGroups,
    ],
  );

  useDispatchPgsAvailability(pgsAvailabilityProps);

  useEffect(() => {
    if (!isResettingBrands) {
      setShowBrandsWarning(false);
    }
    // Because of the way Trading Assistant page works, whenever appliedFilters change,
    // appliedCountries and appliedProductGroups get updated as well, which means that
    // whenever any non related filter gets applied, the deep dive view gets reset back
    // to overview. This logic checks if the appliedCountries and appliedProductGroups
    // actually changed before resetting the deep dive view.
    if (
      prevCountriesAndPGs &&
      (!isEqual(prevCountriesAndPGs.appliedCountries, appliedCountries) ||
        !isEqual(prevCountriesAndPGs.appliedProductGroups, appliedProductGroups))
    ) {
      setIsDeepDiveView(!!viewingBrand.trim());
      setFeatureOptions(INITIAL_BY_SEGMENT_OPTION);
    }
  }, [appliedCountries, appliedProductGroups, isResettingBrands]);

  useEffect(() => {
    if (getBrandsInFilter.length > MAX_BRANDS_FILTER) {
      clearBrandsInFilters();
      setShowBrandsWarning(true);
      setIsResettingBrands(true);
    }
  }, [brandFilterOptions, pagesConfig.appliedFilters]);

  useEffect(() => {
    const isDeepDive: boolean = featureOption.filterKey !== FilterKeys.SELECT;
    setIsDeepDiveView(isDeepDive);

    if (isDeepDive) {
      loadBrandDeepDive();
    }
  }, [featureOption.value]);

  useEffect(() => {
    if (isDeepDiveView) {
      const newDeepDiveDataLookup = createDataLookup(deepDiveChartData?.dataLookup);
      setDeepDiveDataLookup(newDeepDiveDataLookup);
      setViewingBrand(
        getSortedDeepDiveBrands(deepDiveChartData, newDeepDiveDataLookup, kpi, sortBy)[0] ??
          deepDiveChartData?.[kpi]?.[0] ??
          '',
      );
    }
  }, [deepDiveChartData]);

  useEffect(() => {
    if (isDeepDiveView) {
      setViewingBrand(
        getSortedDeepDiveBrands(deepDiveChartData, deepDiveDataLookup, kpi, sortBy)[0] ??
          deepDiveChartData?.[kpi]?.[0] ??
          '',
      );
    }
  }, [kpi]);

  const getLoadDataParams = (): BrandDependencyReqParam | undefined => {
    const countries = [
      appliedCountry(
        pagesConfig,
        PageType.TRADING_ASSISTANT,
        pagesConfig.marketPremiumClientCountries,
        effectiveProductType,
      )!,
    ];
    const productGroup = getAppliedFiltersFromPagesConfig(
      pagesConfig,
      FilterKeys.PRODUCT_GROUP,
      PageType.TRADING_ASSISTANT,
    ) ?? [];

    if (!countries) {
      return;
    }

    return {
      clientId: pagesConfig.clientId,
      countries,
      periodType: timeFrame,
      period: period.value,
      ...(featureOption.filterKey !== FilterKeys.SELECT &&
        featureOption.feature !== NO_OPTIONS && {
          segmentByDataType: featureOption.dataType,
          segmentBy: featureOption.feature,
        }),
      comparisonPeriod: comparisonPeriod as RelativePeriod,
      productType: effectiveProductType,
      productDataType: ProductDataType.PerformanceAnalyzer,
      productGroup,
      appliedFilters: [...pagesConfig.appliedFilters[PageType.TRADING_ASSISTANT].selectedFilters],
    };
  };

  const loadData = async <T,>(
    loadDataFunc: (req: BrandDependencyReqParam) => Promise<T>,
    chartSetter: (data: T) => void,
  ) => {
    setLoadingChart(true);
    try {
      const req = getLoadDataParams();
      if (!req) {
        setLoadingChart(false);
        return;
      }
      const data = await loadDataFunc(req);
      if (data) {
        chartSetter(data);
      }
      setLoadingChart(false);
    } catch (err: any) {
      chartSetter({} as T);
      setLoadingChart(false);
      saveErrorLog(err, 'Brand Dependency chart - Unable to get brand dependency service.', {
        category: ErrorTagEnum.ApiError,
        requestPayload: {
          clientId: retailerClientState.clientId,
          productGroup: appliedProductGroups,
          period: period.value,
          comparisonPeriod: comparisonPeriod,
        },
      });
    }
  };

  const loadBrandDependency = async () => {
    loadData(getBrandDependency, setChartData);
  };

  const loadBrandDeepDive = async () => {
    loadData(getBrandDeepDive, setDeepDiveChartData);
  };

  useEffect(() => {
    if (isDeepDiveView) {
      loadBrandDeepDive();
    } else {
      loadBrandDependency();
    }
  }, [period, comparisonPeriod, pagesConfig.appliedFilters, isDeepDiveView, pagesConfig.clientId]);

  const filteredScopeData = getCountryFilterPairsByTimeFrame(pgListByCountry, timeFrame);

  const dataArr: BrandDependencyChartSubResp[] = getBrandDependencyChartData(getFilteredBrands, kpi, chartData) ?? [];

  const sortedDeepDiveBrands: string[] = getSortedDeepDiveBrands(deepDiveChartData, deepDiveDataLookup, kpi, sortBy);
  const createInsightDataFn: CreateInsightDataFn<TradingAssistantButterflyChartInsight> = (
    collectionDataType = 'live',
  ) => {
    return {
      apiParams: {
        ...getLoadDataParams(),
      },
      growthType: selectedGrowthType,
      factRevenue: factRevenueBySelectedCurrency,
      collectionDataType,
      retailerClientName: retailerClientState.clientName,
      insightCardTitle: TRADING_ASSISTANT_CARD_TITLE,
      selectedGrowthType: selectedGrowthType,
      currencySymbol: factRevenueBySelectedCurrency.currencySymbol,
      brandFilterOptions: brandFilterOptions,
      kpi: kpi,
      sortBy: sortBy,
      appliedCountries: appliedCountries,
      baseTimePeriod: period,
      timeFrame: timeFrame,
      comparisonPeriod: comparisonPeriod,
      appliedProductGroups: appliedProductGroups,
      featureOption: featureOption,
      isDeepDiveView: isDeepDiveView,
      features: features,
      currentViewingBrand: viewingBrand,
      deepDiveBrands: sortedDeepDiveBrands,
      deepDiveDataLookup: deepDiveDataLookup,
      effectiveProductType: effectiveProductType,
    };
  };

  let finalAppliedFilters: Filters[] = cloneDeep([...pagesConfig.appliedFilters[pagesConfig.pageType].selectedFilters]);
  const appliedBrandFilter: Filters | undefined = finalAppliedFilters.find(
    (appliedFilter) => appliedFilter.filterKey === FilterKeys.BRAND,
  );

  let appliedDeepDiveBrandFilter: Option | undefined;

  if (isDeepDiveView && sortedDeepDiveBrands.length > 1) {
    appliedDeepDiveBrandFilter = { label: viewingBrand, value: viewingBrand };
  }

  // If no brand is selected, then the brand should be removed from the applied filters before
  // being passed into the useFetchsalesPerformance hook. If brand is not removed from the applied
  // filters, an empty brand list will be sent in the request to the API, causing the API to return
  // empty result

  if (appliedBrandFilter?.options.length === 0) {
    finalAppliedFilters = finalAppliedFilters.filter((appliedFilter) => appliedFilter.filterKey !== FilterKeys.BRAND);
  }

  /*
   * calls API and updates the performance cards and markets table when new filters are applied
   */
  useFetchSalesPerformance({
    productType: effectiveProductType,
    timeFrame,
    period,
    comparisonPeriod,
    defaultFilters,
    clientId: retailerClientState.clientId,
    setLoadingPosFacts,
    setVolumePerformance: setVolumePerformance,
    setRevenuePerformance,
    appliedFilters: finalAppliedFilters,
    clientCountries: pagesConfig.marketPremiumClientCountries,
    pageType: PageType.TRADING_ASSISTANT,
    appliedDeepDiveBrandFilter: appliedDeepDiveBrandFilter,
    setIsApiErrorCallback: setIsApiError,
  });

  return (
    <>
      <div className="flex items-end pt-6 pb-4">
        <div className="grid grid-flow-col auto-cols-max gap-x-3">
          <BrandSelector
            brandFilter={brandFiltersWithDefaultValue}
            selectedBrands={getFilteredBrands}
            onBrandsChangeFunc={onBrandsChangeFunc}
            isLoadingBrands={isLoadingBrands}
            isVirtualBrandList={isVirtualBrandList}
          />
          <TimeFramePickerWrapper
            currentPeriodicity={timeFrame}
            currentComparison={comparisonPeriod}
            currentPeriod={period}
            periodOptions={periodOptions}
            applyPickerCallback={applyTimeFrameCallback}
            isTimeFramePickerDisabled={false}
            isPeriodOptionsDisabled={false}
            appliedCountries={appliedCountries}
            appliedProductGroups={appliedProductGroups}
            customMonthsPeriod={customMonthsPeriodState!}
            customWeeksPeriod={customWeeksPeriodState!}
          />
          <ComparisonGrowthSelector
            growthOptions={filters?.comparisonGrowth}
            growthOption={onChangeSelectedGrowthType}
            selectedGrowthOption={selectedGrowthType}
            isDisabled={false}
          />
        </div>
      </div>
      {<PgAvailabilityBanner />}
      <FilterSelectionNote
        selectedPeriod={period}
        comparisonPeriodType={comparisonPeriod}
        selectedPeriodicity={timeFrame}
        scopeMetadata={filteredScopeData[0]?.scopeMetadata}
      />
      {showBrandsWarning && <WarningBrandsContainer />}
      <div className="pt-4">
        <FilterChips pgListByCountry={pgListByCountry} selectedTimeFrame={timeFrame} />
      </div>
      <div className="pt-6">
        <MarketPerformancePosFactsTiles
          revenuePerformance={revenuePerformance}
          volumePerformance={volumePerformance}
          selectedGrowthType={selectedGrowthType}
          isLoading={loadingPosFacts}
          factRevenue={factRevenueBySelectedCurrency}
          volume={getVolumeBasedOnPeriodType(filters, timeFrame)}
          subSectionForAmplitudeTracking={SubSection.TRADING_ASSISTANT_SUPPLIER_REVIEW}
          isApiError={isApiError}
        />

        <BrandDependencyCard
          isLoading={loadingChart}
          setIsloading={setLoadingChart}
          chartData={dataArr}
          kpi={kpi}
          setKpi={setKpi}
          sortBy={sortBy}
          setSortBy={setSortBy}
          retailerClientName={retailerClientState.clientName}
          selectedGrowthType={selectedGrowthType}
          factRevenue={factRevenueBySelectedCurrency}
          features={features}
          bySegment={featureOption}
          isDeepDiveView={isDeepDiveView}
          viewingBrand={viewingBrand}
          setViewingBrand={setViewingBrand}
          deepDiveBrands={sortedDeepDiveBrands}
          deepDiveDataLookup={deepDiveDataLookup}
          setFeatureOptions={setFeatureOptions}
          subSectionForAmplitudeTracking={SubSection.TRADING_ASSISTANT_SUPPLIER_REVIEW}
          appliedCountries={appliedCountries}
          period={period}
          timeFrame={timeFrame}
          comparisonPeriod={comparisonPeriod}
          appliedProductGroups={appliedProductGroups}
          featureOption={featureOption}
          effectiveProductType={effectiveProductType}
          createInsightDataFn={createInsightDataFn}
          filters={filters}
          selectedBrands={getFilteredBrands}
          createViewStateFn={createViewState}
          isApiError={isApiError}
        />
      </div>
    </>
  );
};

export default SupplierReview;
